import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import {
  Image,
  VideoPlayer,
  constructSizes,
  Carousel
} from "@lululemon/ui-library";

import { MAX_GRID_WIDTH } from "../../../common/lib/breakpoints";
import { createEncodedKey } from "../../../utils/helpers";

import styles from "./product-education-accordion-media-item.module.scss";

function ProductEducationAccordionMediaItem({ accordionIndex, media }) {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const imageSizes = constructSizes([
    { breakpoint: MAX_GRID_WIDTH, slotSize: "424px" },
    { breakpoint: "992px", slotSize: "29.4vw" },
    { slotSize: "95vw" }
  ]);

  const isCarousel = media.length > 1;

  const imageAndVideoMedia = media.map((mediaItem, index) => {
    const ariaDescribedBy = mediaItem.captionText
      ? `product-education-accordions-${accordionIndex}__aside__media__caption-${index}`
      : undefined;
    const isLandscapeAdaptiveSource = !!mediaItem.videoSrc?.includes(".m3u8");
    const isPortraitAdaptiveSource =
      !!mediaItem.videoSrcPortrait?.includes(".m3u8");

    return (
      <div
        key={`product-education-accordions__imageAndVideoMedia--${createEncodedKey(
          mediaItem
        )}`}
        aria-describedby={ariaDescribedBy}
        className={styles.media}
        data-testid="media"
      >
        {!!mediaItem.videoSrc && (
          <VideoPlayer
            type="stacked"
            allowFullscreen
            analyticsTitle={mediaItem.imageAlt}
            landscapeSource={
              !isLandscapeAdaptiveSource ? mediaItem.videoSrc : null
            }
            landscapeAdaptiveSource={
              isLandscapeAdaptiveSource ? mediaItem.videoSrc : null
            }
            landscapePosterImageProps={{
              src: mediaItem.videoPosterSrc,
              alt: mediaItem.imageAlt
            }}
            portraitSource={
              !isPortraitAdaptiveSource ? mediaItem.videoSrcPortrait : null
            }
            portraitAdaptiveSource={
              isPortraitAdaptiveSource ? mediaItem.videoSrcPortrait : null
            }
            portraitPosterImageProps={{
              alt: mediaItem.imageAlt,
              src: mediaItem.videoPosterSrcPortrait || mediaItem.videoPosterSrc
            }}
            prefetch={false}
          />
        )}
        {!!mediaItem.imageSrc && (
          <Image
            className={styles.image}
            src={mediaItem.imageSrc}
            alt={mediaItem.imageAlt}
            sizes={imageSizes}
          />
        )}
      </div>
    );
  });

  return (
    <div
      data-testid="aside"
      className={styles.aside}
      id={`product-education-accordions__carousel-${accordionIndex}`}
    >
      {isCarousel ? (
        <>
          <Carousel
            showArrows
            showPagination
            onSlideIndexChange={(index) => setActiveSlideIndex(index)}
          >
            {imageAndVideoMedia}
          </Carousel>
        </>
      ) : (
        imageAndVideoMedia
      )}
      <div
        aria-atomic="true"
        aria-live="polite"
        className={styles.captionWrapper}
        data-testid="caption-wrapper"
        role="region"
      >
        {isCarousel && (
          <span className="sr-only">
            {`Slide ${activeSlideIndex + 1} of ${media.length}`}
          </span>
        )}
        {media.map((mediaItem, index) => {
          const isSlideActive = index === activeSlideIndex;
          return (
            mediaItem.captionText && (
              <p
                key={`product-education-accordions__caption--${createEncodedKey(
                  mediaItem
                )}`}
                data-testid="caption"
                aria-hidden={!isSlideActive}
                className={classNames(styles.caption, {
                  "animated fadeIn": isSlideActive,
                  [styles.active]: isSlideActive
                })}
                id={`product-education-accordions-${accordionIndex}__aside__media__caption-${index}`}
              >
                {mediaItem.captionText}
              </p>
            )
          );
        })}
      </div>
    </div>
  );
}

ProductEducationAccordionMediaItem.defaultProps = {
  accordionIndex: 0,
  media: []
};

ProductEducationAccordionMediaItem.propTypes = {
  accordionIndex: PropTypes.number,
  currentBreakpoint: PropTypes.string.isRequired,
  media: PropTypes.arrayOf(PropTypes.object)
};

export default ProductEducationAccordionMediaItem;
